import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 7.5,
    color: '#1f2937'
  },
  headerBanner: {
    backgroundColor: '#1e3a8a',
    color: '#ffffff',
    padding: 8,
    borderRadius: 3,
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#ffffff',
    textTransform: 'uppercase'
  },
  headerSubtitle: {
    fontSize: 7.5,
    color: '#93c5fd',
    marginTop: 2
  },
  certBadge: {
    backgroundColor: '#ffffff',
    color: '#1e3a8a',
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderRadius: 2,
    fontSize: 8,
    fontWeight: 'bold'
  },
  grid2: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6
  },
  grid3: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6
  },
  panel: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#f8fafc'
  },
  panelTitle: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#1e3a8a',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingBottom: 2,
    marginBottom: 4,
    textTransform: 'uppercase'
  },
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 2.5,
    alignItems: 'flex-start'
  },
  fieldLabel: {
    fontSize: 7,
    fontWeight: 'bold',
    color: '#475569',
    width: '45%'
  },
  fieldValue: {
    fontSize: 7,
    color: '#0f172a',
    width: '55%'
  },
  highlightBox: {
    backgroundColor: '#eff6ff',
    borderWidth: 1,
    borderColor: '#bfdbfe',
    borderRadius: 3,
    padding: 5,
    marginBottom: 6
  },
  table: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 2,
    marginBottom: 6
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#e2e8f0',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingVertical: 3,
    paddingHorizontal: 4
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
    paddingVertical: 2.5,
    paddingHorizontal: 4
  },
  tableCellHeader: {
    fontSize: 6.5,
    fontWeight: 'bold',
    color: '#334155'
  },
  tableCell: {
    fontSize: 6.5,
    color: '#0f172a'
  },
  statusPass: {
    color: '#15803d',
    fontWeight: 'bold'
  },
  statusFail: {
    color: '#b91c1c',
    fontWeight: 'bold'
  },
  signaturesPanel: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#cbd5e1',
    paddingTop: 6
  },
  signatureBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#ffffff'
  },
  signatureImage: {
    height: 35,
    maxWidth: 160,
    objectFit: 'contain',
    marginVertical: 2
  },
  footer: {
    marginTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  footerText: {
    fontSize: 6,
    color: '#64748b'
  }
});

const UnventedHotWaterStorageVesselCommissioningInspectionPDF = ({ formData = {} }) => {
  const business = formData.business || {};
  const job = formData.jobAddress || {};
  const landlord = formData.landlordAgent || {};
  const vessel = formData.vesselDetails || {};
  const heatSource = formData.heatSource || {};
  const op1 = formData.operatingDetails1 || {};
  const op2 = formData.operatingDetails2 || {};
  const d2 = formData.dischargePipework || {};
  const defects = formData.defects || {};
  const work = formData.workCarriedOut || {};
  const received = formData.receivedBy || {};
  const issued = formData.issuedBy || {};

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="portrait">
        {/* Header */}
        <View style={styles.headerBanner}>
          <View>
            <Text style={styles.headerTitle}>Unvented Hot Water Storage Vessel Record</Text>
            <Text style={styles.headerSubtitle}>Building Regulations Approved Document G3 (Section 3) Commissioning & Inspection</Text>
          </View>
          <View style={styles.certBadge}>
            <Text>COMPLIANCE CERTIFICATE</Text>
          </View>
        </View>

        {/* Top 3 Addresses */}
        <View style={styles.grid3}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>1. Registered Business</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Business:</Text>
              <Text style={styles.fieldValue}>{business.businessName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Address:</Text>
              <Text style={styles.fieldValue}>{business.businessAddress || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Tel:</Text>
              <Text style={styles.fieldValue}>{business.businessTel || 'N/A'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>2. Job / Site Address</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Site Name:</Text>
              <Text style={styles.fieldValue}>{job.jobName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Address:</Text>
              <Text style={styles.fieldValue}>{job.jobAddress || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Tel:</Text>
              <Text style={styles.fieldValue}>{job.jobTel || 'N/A'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>3. Landlord / Client</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Name:</Text>
              <Text style={styles.fieldValue}>{landlord.landlordName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Address:</Text>
              <Text style={styles.fieldValue}>{landlord.landlordAddress || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Tel:</Text>
              <Text style={styles.fieldValue}>{landlord.landlordTel || 'N/A'}</Text>
            </View>
          </View>
        </View>

        {/* Vessel & Heat Source Details */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>4. Vessel Specification</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Make & Model:</Text>
              <Text style={styles.fieldValue}>{vessel.make || 'N/A'} {vessel.model || ''}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Location:</Text>
              <Text style={styles.fieldValue}>{vessel.location || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Capacity:</Text>
              <Text style={styles.fieldValue}>{vessel.capacity ? `${vessel.capacity} Litres` : 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Makers Warning Label:</Text>
              <Text style={[styles.fieldValue, vessel.makersWarningLabel === 'Yes' ? styles.statusPass : styles.statusFail]}>
                {vessel.makersWarningLabel || 'N/A'}
              </Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Scale Reducer Fitted:</Text>
              <Text style={styles.fieldValue}>{vessel.scaleReducerFitted || 'N/A'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>5. Heat Source & Supply Inlet</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Heat Source Type:</Text>
              <Text style={styles.fieldValue}>{heatSource.heatSourceType || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Inlet Pressure / Flow:</Text>
              <Text style={styles.fieldValue}>{formData.installing?.inletWaterPressureFlowRate || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Fully Commissioned:</Text>
              <Text style={[styles.fieldValue, formData.commissioning?.fullyCommissioned === 'Yes' ? styles.statusPass : styles.statusFail]}>
                {formData.commissioning?.fullyCommissioned || 'N/A'}
              </Text>
            </View>
          </View>
        </View>

        {/* Operating Pressures & Safety Relief Settings */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>6. Operating Parameters & G3 Safety Relief Valves</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>System Operating Pressure:</Text>
                <Text style={styles.fieldValue}>{op1.systemOperatingPressure ? `${op1.systemOperatingPressure} Bar` : 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Expansion Vessel Charge:</Text>
                <Text style={styles.fieldValue}>{op1.operatingPressureExpansionVessel ? `${op1.operatingPressureExpansionVessel} Bar` : 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Expansion Valve Rating:</Text>
                <Text style={styles.fieldValue}>{op1.operatingPressureExpansionValve ? `${op1.operatingPressureExpansionValve} Bar` : 'N/A'}</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Temp/Pressure Relief Valve:</Text>
                <Text style={styles.fieldValue}>{op1.operatingTempReliefValve ? `${op1.operatingTempReliefValve} °C / Bar` : 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Max Primary Circuit:</Text>
                <Text style={styles.fieldValue}>{op1.maxPrimaryCircuitPressureFlow || 'N/A'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* G3 Discharge Pipework (D1, Tundish, D2) */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>7. G3 Statutory Discharge System (D1, Tundish & D2 Termination)</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Nominal D1 Size:</Text>
                <Text style={styles.fieldValue}>{op2.nominalSizeD1 ? `${op2.nominalSizeD1} mm` : 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>D1 Length (max 600mm):</Text>
                <Text style={styles.fieldValue}>{op2.lengthD1 ? `${op2.lengthD1} mm` : 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Tundish In Same Location:</Text>
                <Text style={[styles.fieldValue, op2.tundishSameLocation === 'Yes' ? styles.statusPass : styles.statusFail]}>
                  {op2.tundishSameLocation || 'N/A'}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Tundish Visible:</Text>
                <Text style={[styles.fieldValue, op2.tundishVisible === 'Yes' ? styles.statusPass : styles.statusFail]}>
                  {op2.tundishVisible || 'N/A'}
                </Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Nominal D2 Size:</Text>
                <Text style={styles.fieldValue}>{d2.nominalSizeD2 ? `${d2.nominalSizeD2} mm` : 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Pipe Material:</Text>
                <Text style={styles.fieldValue}>{d2.pipeworkMaterial || 'Copper / Metallic'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Min 300mm Vertical Drop:</Text>
                <Text style={[styles.fieldValue, d2.pipeworkMinVerticalLength === 'Yes' ? styles.statusPass : styles.statusFail]}>
                  {d2.pipeworkMinVerticalLength || 'N/A'}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Continuous Fall (min 1:200):</Text>
                <Text style={[styles.fieldValue, d2.pipeworkFallContinuously === 'Yes' ? styles.statusPass : styles.statusFail]}>
                  {d2.pipeworkFallContinuously || 'N/A'}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Termination Method:</Text>
                <Text style={styles.fieldValue}>{d2.methodOfTermination || 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Termination Satisfactory:</Text>
                <Text style={[styles.fieldValue, d2.terminationSatisfactory === 'Yes' ? styles.statusPass : styles.statusFail]}>
                  {d2.terminationSatisfactory || 'N/A'}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Defects & Work Carried Out */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>8. Defects Identified</Text>
            <Text style={{ fontSize: 6.5, color: defects.defectsIdentified ? '#b91c1c' : '#475569' }}>
              {defects.defectsIdentified || 'None identified. System complies with G3 standards.'}
            </Text>
          </View>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>9. Work Carried Out</Text>
            <Text style={{ fontSize: 6.5, color: '#0f172a' }}>
              {work.workCarriedOut || 'Standard statutory inspection and safety check performed.'}
            </Text>
          </View>
        </View>

        {/* Signatures */}
        <View style={styles.signaturesPanel}>
          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Commissioning / Inspecting Engineer</Text>
            {issued.engineerSignature ? (
              <Image src={issued.engineerSignature} style={styles.signatureImage} />
            ) : (
              <View style={{ height: 35, justifyContent: 'center' }}>
                <Text style={{ fontSize: 6.5, color: '#94a3b8' }}>[Signature Stamped Electronically]</Text>
              </View>
            )}
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{issued.issuedByDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>

          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Client / Customer Acceptance</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Name:</Text>
              <Text style={styles.fieldValue}>{received.receivedByName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{received.receivedByDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
            <Text style={{ fontSize: 5.5, color: '#64748b', marginTop: 4 }}>
              I acknowledge receipt of this G3 Compliance Record and confirm system handover.
            </Text>
          </View>
        </View>

        {/* Statutory Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            UK Building Regulations 2010 (Approved Document G3) • Unvented Hot Water Safety Record
          </Text>
          <Text style={styles.footerText}>
            Generated: {new Date().toLocaleDateString('en-GB')}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default UnventedHotWaterStorageVesselCommissioningInspectionPDF;
